import * as React from "react"

export type ToastVariant = "default" | "success" | "info" | "warning" | "destructive";

export interface Toast {
  id: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactElement;
  variant?: ToastVariant;
  duration?: number;
}

interface ToastContextType {
  toasts: Toast[];
  toast: (props: Omit<Toast, "id">) => { id: string };
  dismiss: (id: string) => void;
}

const ToastContext = React.createContext<ToastContextType>({
  toasts: [],
  toast: () => ({ id: "" }),
  dismiss: () => {},
});

// Generate a unique ID for each toast
function generateId() {
  return Math.random().toString(36).substring(2, 9);
}

interface ToastProviderProps {
  children: React.ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = React.useState<Toast[]>([]);
  
  // Toast dismissal function
  const dismiss = React.useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);
  
  // Toast creation function
  const showToast = React.useCallback((props: Omit<Toast, "id">) => {
    const id = generateId();
    
    // Set default duration based on variant
    let duration = props.duration;
    duration ??= props.variant === "destructive" ? 6000 : 4000;
    
    const newToast = { 
      ...props,
      id,
      variant: props.variant ?? "default",
      duration
    };
    
    setToasts((current) => [...current, newToast]);
    
    // Auto-dismiss after duration unless it's Infinity
    if (duration !== Infinity) {
      setTimeout(() => {
        dismiss(id);
      }, duration);
    }
    
    return { id };
  }, [dismiss]);
  
  // Listen for toast events from outside React components
  React.useEffect(() => {
    const handleToastEvent = (e: Event) => {
      const event = e as CustomEvent<Omit<Toast, "id">>;
      if (event.detail) {
        showToast(event.detail);
      }
    };
    
    document.addEventListener("toast", handleToastEvent);
    return () => {
      document.removeEventListener("toast", handleToastEvent);
    };
  }, [showToast]);
  
  const contextValue = React.useMemo<ToastContextType>(() => ({ 
    toasts, 
    toast: showToast, 
    dismiss 
  }), [toasts, showToast, dismiss]);

  return React.createElement(
    ToastContext.Provider,
    { value: contextValue },
    children
  );
}

// Custom hook to use the toast system
export function useToast() {
  const context = React.useContext(ToastContext);
  
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  
  return context;
}

// Standalone toast function that doesn't require the hook
export const toast = (props: Omit<Toast, "id">) => {
  const event = new CustomEvent("toast", { 
    detail: props
  });
  
  document.dispatchEvent(event);
  
  return {
    id: "",
    dismiss: () => {},
    update: () => {}
  };
};
